import { Link, useParams } from 'react-router-dom';
import { useSeo } from '../../hooks/useSeo';
import { BlogLayout } from '../blog/BlogLayout';
import { getPost } from '../blog/registry';
import { ENTITIES } from '../docs/data';
import {
  GLOSSARY,
  buildGlossaryIndexJsonLd,
  buildTermJsonLd,
  getTerm,
  type GlossaryTerm,
} from './glossary';

const INDEX_DESCRIPTION =
  'Plain-English definitions of the SaaS revenue terms that matter: MRR, ARR, NRR, churn, expansion, ARPA, LTV, dunning and more, with formulas and further reading.';

function groupByLetter(terms: GlossaryTerm[]) {
  const groups = new Map<string, GlossaryTerm[]>();
  for (const t of terms) {
    const letter = t.name[0].toUpperCase();
    groups.set(letter, [...(groups.get(letter) ?? []), t]);
  }
  return [...groups.entries()];
}

export function GlossaryIndex() {
  useSeo({
    title: 'SaaS Revenue Glossary — Revvly',
    description: INDEX_DESCRIPTION,
    path: '/glossary',
    type: 'website',
    jsonLd: buildGlossaryIndexJsonLd(),
  });

  const groups = groupByLetter(GLOSSARY);
  const concepts = Object.values(ENTITIES);

  return (
    <BlogLayout>
      <header className="rv-hero">
        <p className="rv-eyebrow">Glossary</p>
        <h1>
          SaaS revenue terms, <em>defined</em>.
        </h1>
        <p>{INDEX_DESCRIPTION}</p>
      </header>

      <div className="rv-prose">
        <nav aria-label="Jump to letter">
          <p>
            {groups.map(([letter]) => (
              <a key={letter} href={`#letter-${letter}`}>
                {letter}{' '}
              </a>
            ))}
          </p>
        </nav>

        {groups.map(([letter, items]) => (
          <section key={letter}>
            <h2 id={`letter-${letter}`}>{letter}</h2>
            {items.map((t) => (
              <div key={t.slug} className="rv-faq-item">
                <h3>
                  <Link to={`/glossary/${t.slug}`}>{t.name}</Link>
                </h3>
                <p>{t.definition}</p>
              </div>
            ))}
          </section>
        ))}

        <h2 id="data-model">How Revvly models these</h2>
        <p>
          These are the objects behind the metrics in Revvly, each with its own reference page in
          the docs.
        </p>
        <ul>
          {concepts.map((c) => (
            <li key={c.id}>
              <Link to={`/docs/concepts/${c.id}`}>{c.name}</Link>: {c.oneLiner}
            </li>
          ))}
        </ul>
      </div>
    </BlogLayout>
  );
}

export function GlossaryTermPage() {
  const { slug } = useParams();
  const term = getTerm(slug);

  useSeo({
    title: term ? `${term.name}: definition and formula — Revvly` : 'Glossary — Revvly',
    description: term ? term.definition : INDEX_DESCRIPTION,
    path: term ? `/glossary/${term.slug}` : '/glossary',
    type: 'article',
    jsonLd: term ? buildTermJsonLd(term) : undefined,
  });

  if (!term) {
    return (
      <BlogLayout>
        <div className="rv-prose">
          <h1>Term not found</h1>
          <p>
            <Link to="/glossary">Browse the full glossary</Link>.
          </p>
        </div>
      </BlogLayout>
    );
  }

  const posts = term.posts.map((s) => getPost(s)).filter((p) => p != null);
  const related = term.related.map((s) => getTerm(s)).filter((t): t is GlossaryTerm => t != null);

  return (
    <BlogLayout>
      <div className="rv-article">
        <article className="rv-body-col">
          <header className="rv-art-head">
            <p className="rv-eyebrow">
              <Link to="/glossary">Glossary</Link>
            </p>
            <h1>{term.name}</h1>
            {term.aka && term.aka.length > 0 && (
              <p className="rv-deck">Also: {term.aka.join(', ')}</p>
            )}
          </header>

          <div className="rv-prose">
            <p>
              <strong>{term.definition}</strong>
            </p>
            {term.formula && (
              <>
                <h2 id="formula">Formula</h2>
                <p>
                  <code>{term.formula}</code>
                </p>
              </>
            )}
            <h2 id="how-to-read-it">How to read it</h2>
            <p>{term.detail}</p>

            {term.docs.length > 0 && (
              <>
                <h2 id="in-revvly">In Revvly</h2>
                <ul>
                  {term.docs.map((d) => (
                    <li key={d}>
                      <Link to={d}>{d.replace('/docs/', 'Docs: ').replace(/[-/]/g, ' ')}</Link>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {posts.length > 0 && (
              <>
                <h2 id="further-reading">Further reading</h2>
                <ul>
                  {posts.map((p) => (
                    <li key={p.slug}>
                      <Link to={`/blog/${p.slug}`}>{p.title.replace(/\*/g, '')}</Link>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {related.length > 0 && (
              <>
                <h2 id="related-terms">Related terms</h2>
                <ul>
                  {related.map((r) => (
                    <li key={r.slug}>
                      <Link to={`/glossary/${r.slug}`}>{r.name}</Link>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </article>
      </div>
    </BlogLayout>
  );
}
